import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FaComments, FaSpinner, FaClock, FaTimes } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';
import Chat from '../components/Chat';
import { listingService } from '../services/listingService';

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem auto;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h1 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid ${props => props.theme.colors.border};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  cursor: pointer;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border-bottom: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.2s ease;
  margin-bottom: -2px;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    padding: 0.65rem 0.75rem;
    font-size: 0.85rem;
  }
`;

const ThreadsList = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ThreadCard = styled.div`
  background: ${props => props.$hasUnread ? props.theme.colors.surfaceAlt : props.theme.colors.surface};
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-left: 4px solid ${props => props.$hasUnread ? '#e74c3c' : props.theme.colors.primary};
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px ${props => props.theme.colors.shadowHover};
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const UnreadBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: #e74c3c;
  color: white;
  border-radius: 12px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: bold;
`;

const ThreadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-right: 3rem; /* Make space for unread badge */
`;

const ThreadInfo = styled.div`
  flex: 1;
  
  h3 {
    color: ${props => props.theme.colors.text};
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    margin: 0;
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    h3 {
      font-size: 1rem;
    }
    
    p {
      font-size: 0.85rem;
    }
  }
`;

const ThreadMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #8B5A96;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  h3 {
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
`;

const ListingModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`;

const ListingModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 10px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 4px 20px ${props => props.theme.colors.shadow};
`;

const ListingModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  position: sticky;
  top: 0;
  background: ${props => props.theme.colors.surface};
  z-index: 1;
  
  h2 {
    color: ${props => props.theme.colors.text};
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  transition: background 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
`;

const ListingModalBody = styled.div`
  padding: 1.5rem;
`;

const ListingDetail = styled.div`
  margin-bottom: 1.5rem;
  
  h3 {
    color: ${props => props.theme.colors.text};
    margin-bottom: 0.5rem;
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.6;
    margin: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const DetailItem = styled.div`
  padding: 0.5rem 0;
  
  strong {
    color: ${props => props.theme.colors.text};
    display: block;
    margin-bottom: 0.25rem;
  }
  
  span {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SkillTag = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  margin: 0.25rem 0.25rem 0.25rem 0;
  font-size: 0.9rem;
  border: 1px solid ${props => props.theme.colors.border};
`;

const Messages = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [activeTab, setActiveTab] = useState('gigs'); // 'gigs', 'ventures', 'posts'
  const [userListings, setUserListings] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [viewingListing, setViewingListing] = useState(null);

  useEffect(() => {
    if (user) {
      loadThreads();
      loadUserListings();
      setupRealtimeSubscription();
    }

    return () => {
      if (subscription) {
        chatService.unsubscribe(subscription);
      }
    };
  }, [user]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const { threads: userThreads } = await chatService.getUserThreads(user.id);
      setThreads(userThreads || []);
      
      // Load unread counts for each thread
      await loadUnreadCounts(userThreads || []);
    } catch (error) {
      console.error('Error loading threads:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCounts = async (threadList) => {
    try {
      const counts = {};
      await Promise.all(
        threadList.map(async (thread) => {
          const { data, error } = await chatService.getThreadUnreadCount(thread.id, user.id);
          if (!error) {
            counts[thread.id] = data || 0;
          }
        })
      );
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Error loading unread counts:', error);
    }
  };

  const loadUserListings = async () => {
    try {
      const { listings } = await listingService.getUserListings(user.id);
      setUserListings(listings || []);
    } catch (error) {
      console.error('Error loading user listings:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const sub = chatService.subscribeToUserThreads(user.id, () => {
      // Reload threads when there are updates
      loadThreads();
    });
    
    setSubscription(sub);
  };

  const handleThreadSelect = async (thread) => {
    setSelectedThread(thread);
    
    // Clear unread count for this thread immediately (optimistic update)
    setUnreadCounts(prev => ({
      ...prev,
      [thread.id]: 0
    }));
  };

  const handleBackToList = async () => {
    setSelectedThread(null);
    
    // Reload threads and unread counts
    await loadThreads();
  };

  const getOtherParticipant = (thread) => {
    if (!thread || !user) return null;
    
    if (thread.seeker_id === user.id) {
      return thread.entrepreneur_profile;
    } else {
      return thread.seeker_profile;
    }
  };

  const getFilteredThreads = () => {
    if (!threads || threads.length === 0) return [];
    
    const userListingIds = userListings.map(l => l.id);
    
    let filtered = [];
    
    switch (activeTab) {
      case 'gigs':
        // Threads where user is seeking and listing is a gig
        filtered = threads.filter(t => 
          t.seeker_id === user.id && 
          t.listings?.type === 'gig' &&
          !userListingIds.includes(t.listing_id)
        );
        break;
      
      case 'ventures':
        // Threads where user is seeking and listing is a hustle
        filtered = threads.filter(t => 
          t.seeker_id === user.id && 
          t.listings?.type === 'hustle' &&
          !userListingIds.includes(t.listing_id)
        );
        break;
      
      case 'posts':
        // Threads where the listing belongs to the user
        filtered = threads.filter(t => userListingIds.includes(t.listing_id));
        break;
      
      default:
        filtered = threads;
    }
    
    // Sort by unread first, then by last message time
    return filtered.sort((a, b) => {
      const aUnread = unreadCounts[a.id] || 0;
      const bUnread = unreadCounts[b.id] || 0;
      
      // If one has unread and the other doesn't, prioritize unread
      if (aUnread > 0 && bUnread === 0) return -1;
      if (aUnread === 0 && bUnread > 0) return 1;
      
      // Otherwise sort by last message time
      return new Date(b.last_message_at) - new Date(a.last_message_at);
    });
  };

  const filteredThreads = getFilteredThreads();
  const gigCount = threads.filter(t => 
    t.seeker_id === user.id && 
    t.listings?.type === 'gig' &&
    !userListings.map(l => l.id).includes(t.listing_id)
  ).length;
  const ventureCount = threads.filter(t => 
    t.seeker_id === user.id && 
    t.listings?.type === 'hustle' &&
    !userListings.map(l => l.id).includes(t.listing_id)
  ).length;
  const postsCount = threads.filter(t => 
    userListings.map(l => l.id).includes(t.listing_id)
  ).length;

  // Notify parent to refresh unread count
  const handleMessagesRead = () => {
    // Trigger a custom event that Header can listen to
    window.dispatchEvent(new CustomEvent('messagesRead'));
  };

  const handleViewListing = (listing) => {
    setViewingListing(listing);
  };

  const handleCloseListingView = () => {
    setViewingListing(null);
  };

  // Show chat interface if a thread is selected
  if (selectedThread) {
    return (
      <>
        <Container>
          <Chat 
            thread={selectedThread} 
            onBack={handleBackToList}
            onMessagesRead={handleMessagesRead}
            onViewListing={handleViewListing}
          />
        </Container>
        
        {viewingListing && (
          <ListingModal onClick={handleCloseListingView}>
            <ListingModalContent onClick={(e) => e.stopPropagation()}>
              <ListingModalHeader>
                <h2>{viewingListing.title}</h2>
                <CloseButton onClick={handleCloseListingView}>
                  <FaTimes />
                </CloseButton>
              </ListingModalHeader>
              <ListingModalBody>
                <ListingDetail>
                  <h3>Description</h3>
                  <p>{viewingListing.short_description}</p>
                </ListingDetail>
                
                <DetailGrid>
                  <DetailItem>
                    <strong>Location</strong>
                    <span>
                      {(() => {
                        const loc = viewingListing.location || viewingListing.filters?.location;
                        if (!loc?.province) return 'Not specified';
                        const displayArea = loc.area === 'Other' ? loc.customArea : loc.area;
                        return displayArea ? `${displayArea}, ${loc.province}` : loc.province;
                      })()}
                    </span>
                  </DetailItem>
                  {viewingListing.type === 'gig' && viewingListing.filters?.dates && viewingListing.filters.dates.length > 0 && (
                    <DetailItem>
                      <strong>Available Dates</strong>
                      <span>
                        {viewingListing.filters.dates.map((date, idx) => (
                          <div key={date} style={{ marginTop: idx > 0 ? '0.25rem' : 0 }}>
                            {new Date(date).toLocaleDateString('en-ZA', { 
                              weekday: 'short',
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        ))}
                      </span>
                    </DetailItem>
                  )}
                  {viewingListing.type !== 'gig' && (
                    <>
                      <DetailItem>
                        <strong>Industry</strong>
                        <span>{viewingListing.industry || viewingListing.filters?.industry || 'Not specified'}</span>
                      </DetailItem>
                      <DetailItem>
                        <strong>Business Age</strong>
                        <span>{viewingListing.age || viewingListing.filters?.age || 'Not specified'}</span>
                      </DetailItem>
                      <DetailItem>
                        <strong>Team Size</strong>
                        <span>{viewingListing.size || viewingListing.filters?.size || 'Not specified'}</span>
                      </DetailItem>
                    </>
                  )}
                  <DetailItem>
                    <strong>{viewingListing.type === 'gig' ? 'Fee' : 'Payment'}</strong>
                    <span>{viewingListing.pay || viewingListing.filters?.pay || 'Not specified'}</span>
                  </DetailItem>
                  {viewingListing.type !== 'gig' && (
                    <DetailItem>
                      <strong>Role Type</strong>
                      <span>{viewingListing.role || viewingListing.filters?.role || 'Not specified'}</span>
                    </DetailItem>
                  )}
                  <DetailItem>
                    <strong>Time Commitment</strong>
                    <span>{viewingListing.time_commitment || viewingListing.filters?.time_commitment || 'Not specified'}</span>
                  </DetailItem>
                  <DetailItem>
                    <strong>Work Structure</strong>
                    <span>{viewingListing.remote || viewingListing.filters?.remote || 'Not specified'}</span>
                  </DetailItem>
                  <DetailItem>
                    <strong>Traveling</strong>
                    <span>{viewingListing.traveling || viewingListing.filters?.traveling ? 'Yes' : 'No'}</span>
                  </DetailItem>
                </DetailGrid>
                
                {(viewingListing.skills || viewingListing.filters?.skills) && 
                 (viewingListing.skills?.length > 0 || viewingListing.filters?.skills?.length > 0) && (
                  <ListingDetail>
                    <h3>Skills Needed</h3>
                    <div>
                      {(viewingListing.skills || viewingListing.filters?.skills || []).map(skill => (
                        <SkillTag key={skill}>{skill}</SkillTag>
                      ))}
                    </div>
                  </ListingDetail>
                )}
                
                {viewingListing.full_story && (
                  <ListingDetail>
                    <h3>The Full Story</h3>
                    <p>{viewingListing.full_story}</p>
                  </ListingDetail>
                )}
              </ListingModalBody>
            </ListingModalContent>
          </ListingModal>
        )}
      </>
    );
  }

  return (
    <Container>
      <Header>
        <h1>Messages</h1>
        <p>Your conversations with other entrepreneurs</p>
      </Header>

      <TabsContainer>
        <Tab 
          active={activeTab === 'gigs'} 
          onClick={() => setActiveTab('gigs')}
        >
          Gigs you're chatting with ({gigCount})
        </Tab>
        <Tab 
          active={activeTab === 'ventures'} 
          onClick={() => setActiveTab('ventures')}
        >
          Hustles you're chatting with ({ventureCount})
        </Tab>
        <Tab 
          active={activeTab === 'posts'} 
          onClick={() => setActiveTab('posts')}
        >
          Posts you have created ({postsCount})
        </Tab>
      </TabsContainer>

      {loading ? (
        <LoadingContainer>
          <FaSpinner size={24} />
          <span style={{ marginLeft: '1rem' }}>Loading conversations...</span>
        </LoadingContainer>
      ) : filteredThreads.length === 0 ? (
        <EmptyState>
          <FaComments size={48} />
          <h3>No conversations in this category</h3>
          <p>
            {activeTab === 'gigs' && 'Gigs you inquire about will appear here.'}
            {activeTab === 'ventures' && 'Ventures you inquire about will appear here.'}
            {activeTab === 'posts' && 'Conversations about your posts will appear here.'}
          </p>
        </EmptyState>
      ) : (
        <ThreadsList>
          {filteredThreads.map(thread => {
            const otherParticipant = getOtherParticipant(thread);
            const unreadCount = unreadCounts[thread.id] || 0;
            const hasUnread = unreadCount > 0;
            
            return (
              <ThreadCard key={thread.id} onClick={() => handleThreadSelect(thread)} $hasUnread={hasUnread}>
                {hasUnread && <UnreadBadge>{unreadCount}</UnreadBadge>}
                <ThreadHeader>
                  <ThreadInfo>
                    <h3>{thread.listings?.title || 'Untitled Venture'}</h3>
                    <p>with {otherParticipant?.display_name || 'Unknown User'}</p>
                  </ThreadInfo>
                  <ThreadMeta>
                    <FaClock />
                    <span>{formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}</span>
                  </ThreadMeta>
                </ThreadHeader>
              </ThreadCard>
            );
          })}
        </ThreadsList>
      )}
    </Container>
  );
};

export default Messages;